<template>
  <div v-if="!isLoading">
    <!-- Organization Information -->
    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.org_info') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.nationality') }}</b>
            <div class="mg-t-11px">
              {{ form.nationality }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.company_name') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCompanyName }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tax_id') }}</b>
            <div class="mg-t-11px">
              {{ form.orgTaxId }}
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.business_type') }}</b>
            <div class="mg-t-11px">
              {{ form.orgBusinessType | displayFromKeyToText(businessList, $i18n.locale) }}
            </div>
          </div>
          <div
            v-if="form.averageIncome"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.avg_income') }}</b>
            <div class="mg-t-11px">
              {{ form.averageIncome | displayFromKeyToText(averageIncomeList, $i18n.locale) }}
            </div>
          </div>
          <div
            v-if="form.proofOfIncome"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.proof_of_income') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.proofOfIncome"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
          <div
            v-if="form.assetOwnership"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.evidence_assets') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.assetOwnership"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.address') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCompanyAddressLineOne }}
            </div>
          </div>

          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCompanyAddressLineTwo || '-' }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCountry }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCompanyProvince | displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ form.orgCompanyCity }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ form.originPostcode }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tel_number') }}</b>
            <div class="mg-t-11px">
              {{ form.orgTel }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Information -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_info') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.name_title') }}</b>
            <div
              v-if="form.nameTitle === 'Mr.'"
              class="mg-t-11px"
            >
              {{ $t('kyc.mr') }}
            </div>
            <div
              v-else-if="form.nameTitle === 'Miss.'"
              class="mg-t-11px"
            >
              {{ $t('kyc.miss') }}
            </div>
            <div
              v-else
              class="mg-t-11px"
            >
              {{ $t('kyc.mrs') }}
            </div>
          </div>
          <div
            v-if="form.firstNameInThai"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.first_name_th') }}</b>
            <div class="mg-t-11px">
              {{ form.firstNameInThai }}
            </div>
          </div>
          <div
            v-if="form.lastNameInThai"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.last_name_th') }}</b>
            <div class="mg-t-11px">
              {{ form.lastNameInThai }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.first_name') }}</b>
            <div class="mg-t-11px">
              {{ form.firstName }}
            </div>
          </div>
          <div
            v-if="form.middleName"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.middle_name_summary') }}</b>
            <div class="mg-t-11px">
              {{ form.middleName }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.last_name') }}</b>
            <div class="mg-t-11px">
              {{ form.lastName }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.gender') }}</b>
            <div class="mg-t-11px">
              {{ form.gender === 'Male' ? $t('kyc.male') : $t('kyc.female') }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.marital_status') }}</b>
            <div class="mg-t-11px">
              {{ form.maritalStatus === 'Single' ? $t('kyc.single') : $t('kyc.married') }}
            </div>
          </div>

          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.tel_number') }}</b>
            <div class="mg-t-11px">
              {{ form.tel }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_birth') }}</b>
            <div class="mg-t-11px">
              {{ form.birthdate | formatDate }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.occupation') }}</b>
            <div class="mg-t-11px">
              {{ form.career | displayFromKeyToText(occupationList, $i18n.locale) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Origin Address -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_addr') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.origin_address') }}</b>
            <div class="mg-t-11px">
              {{ form.originAddressLineOne }}
            </div>
          </div>
          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ form.originAddressLineTwo || '-' }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ form.originCountry }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ form.originProvince | displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ form.originCity }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ form.originPostcode }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Current Address -->

    <div class="columns">
      <div class="column">
        <div class="pd-l-12pct">
          <div class="mg-t-31px">
            <b>{{ $t('kyc.current_address') }}</b>
            <div class="mg-t-11px">
              {{ form.currentAddressLineOne }}
            </div>
          </div>
          <div class="mg-t-31px">
            <b>{{ $t('kyc.address_line_2_summary') }}</b>
            <div class="mg-t-11px">
              {{ form.currentAddressLineTwo || '-' }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.country') }}</b>
            <div class="mg-t-11px">
              {{ form.currentCountry }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.province') }}</b>
            <div class="mg-t-11px">
              {{ form.currentProvince | displayFromKeyToText(provincesInThailand, $i18n.locale) }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.city') }}</b>
            <div class="mg-t-11px">
              {{ form.currentCity }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.zipcode') }}</b>
            <div class="mg-t-11px">
              {{ form.currentPostcode }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="divider"/>

    <!-- Authorized Representative Identity -->

    <div class="columns">
      <div class="column">
        <div class="tt">
          {{ $t('kyc.auth_represent_identity') }}
        </div>
        <div class="pd-l-12pct">
          <div class="mg-t-40px">
            <b>{{ $t('kyc.type_of_card') }}:</b> {{ form.typeOfCard === 'ID Card' ? $t('kyc.id_card') : $t('kyc.passport') }}
          </div>
          <div class="mg-t-40px is-half">
            <b>{{ form.typeOfCard === 'ID Card' ? $t('kyc.id_card') : $t('kyc.passport') }}</b>
            <div class="mg-t-11px">
              {{ form.identificationId }}
            </div>
          </div>
          <div
            v-if="form.laserCode"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.laser_code') }}</b>
            <div class="mg-t-11px">
              {{ form.laserCode }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_issue') }}</b>
            <div class="mg-t-11px">
              {{ form.issueDateOfId | formatDate }}
            </div>
          </div>
          <div class="mg-t-31px is-half">
            <b>{{ $t('kyc.date_of_exp') }}</b>
            <div class="mg-t-11px">
              {{ form.expireDateOfId | formatDate }}
            </div>
          </div>
          <div class="mg-t-40px">
            <b>{{ $t('kyc.photo_id_card') }}</b>
            <div class="mw-600">
              <img
                :src="form.identificationPicture"
                class="mg-t-21px"
              >
            </div>
          </div>
          <div class="mg-t-40px">
            <b>{{ $t('kyc.photo_selfie') }}</b>
            <div class="mw-600">
              <img
                :src="form.selfIdentificationPicture"
                class="mg-t-21px"
              >
            </div>
          </div>
          <div
            v-if="form.orgCompanyCert"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.comp_cert_doc') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.orgCompanyCert"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
          <div
            v-if="form.orgShareholders"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.list_shareholders_doc') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.orgShareholders"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
          <div
            v-if="form.orgSealCert"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.seal_cert_doc') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.orgSealCert"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
          <div
            v-if="form.orgHouseRegisAuthDirector"
            class="mg-t-31px is-half"
          >
            <b>{{ $t('kyc.copy_house_registration') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.orgHouseRegisAuthDirector"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
          <div
            v-if="form.orgPowerAttorney"
            class="mg-t-31px"
          >
            <b>{{ $t('kyc.power_attorney_lowercase') }}</b>
            <div class="mg-t-11px">
              <a
                :href="form.orgPowerAttorney"
                target="_blank"
                class="doc-file-link"
              >
                {{ $t('messages.click_to_see_doc') }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <div
      v-for="(index) in 15"
      :key="index"
      class="level is-mobile"
    >
      <div
        v-for="(item) in 2"
        :key="item"
        class="level-item"
      >
        <b-skeleton height="25px"/>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Summary',
  inject: ['$validator'],
  props: {
    user: {
      type: Object,
      default: () => {}
    },
    form: {
      type: Object,
      default: () => {}
    },
    provincesInThailand: {
      type: Array,
      default: () => []
    },
    occupationList: {
      type: Array,
      default: () => []
    },
    businessList: {
      type: Array,
      default: () => []
    },
    averageIncomeList: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style lang="scss" scoped>
* {
  font-size: 15px;
  color: #000000;
}
.status {
  color: #f15c25;
  border: 1px solid #f15c25;
  padding: 5px 20px;
  border-radius: 25px;
  font-size: 13px;
  margin-left: 10px;
}
.tt {
  font-family: Roboto;
  font-size: 21px;
  font-weight: bold;
}
.mg-t-11px {
  margin-top: 11px;
}
.mg-t-21px {
  margin-top: 21px;
}
.mg-t-31px {
  margin-top: 31px;
}
.mg-t-40px {
  margin-top: 40px;
}
.mg-t-80px {
  margin-top: 80px;
}
.pd-l-12pct {
  padding-left: 12%;
}
.mw-600 {
  max-width: 600px;
}
.is-half {
  display: inline-block;
  width: calc((100% - 12%) / 2);
}
.divider {
  width: 100%;
  height: 1px;
  background-color: #d2d2d2;
  margin: 40px 0;
}
.doc-file-link {
  color: #0564c4;
  margin-top: 5px;
  font-weight: bold;
  text-decoration: underline;
}
/* Mobile */
@media only screen and (max-width: 768px) {
  .pd-l-12pct {
    padding-left: 4%;
  }
  .is-half {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/SummaryOfKYC/components/SummaryOrganization.vue